import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Moveable from 'react-moveable';
import type { OnDrag, OnDragGroup, OnDragGroupEnd } from 'react-moveable';

import { clearSelection, setActiveTool, setZoom } from '../slideEditorSlice';
import { useMoveable } from '../hooks/useMoveable';

import SlideElement from './SlideElement';
import VideoSettingsControl from './VideoSettingsControl';

import type { ResolvedTheme, Slide, VideoElement } from '@/shared/types/slide';
import { SLIDE_PAGE_WIDTH, SLIDE_PAGE_HEIGHT } from '@/shared/types/slide';
import { buildRenderElements, resolveSlideBackground } from '@/shared/theming/resolve';
import type { ResolvedSlideElement } from '@/shared/theming/resolve';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux';

// ---------------------------------------------------------------------------
// SlideCanvas
//
// Renders the selected slide at SLIDE_PAGE_WIDTH × SLIDE_PAGE_HEIGHT scaled
// by the zoom factor.  Provides click-to-select, click-to-deselect, and
// Moveable handles (drag / resize / rotate) for the selected element.
// ---------------------------------------------------------------------------

interface SlideCanvasProps {
  currentSlide: Slide | null;
  theme: ResolvedTheme;
  slideElements: ReturnType<typeof import('../hooks/useSlideElements').useSlideElements>;
  canEdit?: boolean;
  /** Called when image tool is active and user clicks the canvas */
  onImageToolClick?: (x: number, y: number) => void;
  /** Called when user double-clicks an image element */
  onImageDoubleClick?: (elementId: string) => void;
  suppressVideoPreview?: boolean;
}

const SlideCanvas: React.FC<SlideCanvasProps> = ({
  currentSlide,
  theme,
  slideElements,
  canEdit = true,
  onImageToolClick,
  onImageDoubleClick,
  suppressVideoPreview = false,
}) => {
  const dispatch = useAppDispatch();
  const zoom = useAppSelector((s) => s.slideEditor.zoom);
  const selectedElementIds = useAppSelector((s) => s.slideEditor.selectedElementIds);
  const activeToolType = useAppSelector((s) => s.slideEditor.activeToolType);
  const activeShapeType = useAppSelector((s) => s.slideEditor.activeShapeType);

  const canvasRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);

  const {
    updateElement,
    updateElements,
    selectElement,
    createTextElement,
    createShapeElement,
    createImageElement,
  } = slideElements;

  // Display source of truth: the resolved view (placeholder geometry/style applied,
  // tokens → raw). Mutations still target stored elements by `id` via the hook.
  // Decoration (`source:'theme'`) is rendered but never interactive (Phase 1: none).
  const elements = useMemo(
    () => (currentSlide ? buildRenderElements(currentSlide, theme) : []),
    [currentSlide, theme]
  );

  const [isInteracting, setIsInteracting] = useState(false);
  const [videoPreviewResetKey, setVideoPreviewResetKey] = useState(0);

  const resetVideoPreviews = useCallback(() => {
    setVideoPreviewResetKey((key) => key + 1);
  }, []);

  // Moveable handlers
  const { getHandlers, handleArrowKey } = useMoveable(
    elements,
    (id, patch) => {
      updateElement(id, patch);
    },
    { onUpdateMany: updateElements }
  );

  const getHandlersTracked = useCallback(
    (id: string) => {
      const handlers = getHandlers(id);
      return {
        ...handlers,
        onDragStart: (e: Parameters<typeof handlers.onDragStart>[0]) => {
          const result = handlers.onDragStart(e);
          if (result !== false) setIsInteracting(true);
          return result;
        },
        onDragEnd: (e: Parameters<typeof handlers.onDragEnd>[0]) => {
          handlers.onDragEnd(e);
          setIsInteracting(false);
        },
        onResizeStart: (e: Parameters<typeof handlers.onResizeStart>[0]) => {
          const result = handlers.onResizeStart(e);
          if (result !== false) setIsInteracting(true);
          return result;
        },
        onResizeEnd: (e: Parameters<typeof handlers.onResizeEnd>[0]) => {
          handlers.onResizeEnd(e);
          setIsInteracting(false);
        },
        onRotateStart: (e: Parameters<typeof handlers.onRotateStart>[0]) => {
          const result = handlers.onRotateStart(e);
          if (result !== false) setIsInteracting(true);
          return result;
        },
        onRotateEnd: (e: Parameters<typeof handlers.onRotateEnd>[0]) => {
          handlers.onRotateEnd(e);
          setIsInteracting(false);
        },
      };
    },
    [getHandlers]
  );

  // Keyboard arrow key nudging
  useEffect(() => {
    if (!canEdit) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // Don't nudge if user is typing in contentEditable or input
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable)
        return;
      handleArrowKey(e, selectedElementIds);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canEdit, handleArrowKey, selectedElementIds]);

  // Scroll-wheel zoom (Ctrl/Cmd + wheel)
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -5 : 5;
      dispatch(setZoom(zoom + delta));
    },
    [dispatch, zoom]
  );

  const selectedTargets = useMemo(
    () =>
      selectedElementIds
        .map(
          (id) =>
            canvasRef.current?.querySelector(
              `[data-element-id="${CSS.escape(id)}"]`
            ) as HTMLElement | null
        )
        .filter(Boolean) as HTMLElement[],
    [selectedElementIds, elements] // elements dep ensures refresh after add/remove
  );

  const selectedPositionKey = selectedElementIds
    .map((id) => {
      const el = elements.find((candidate) => candidate.id === id);
      return el
        ? `${id}:${el.position.x},${el.position.y},${el.position.width},${el.position.height},${el.position.rotation}`
        : id;
    })
    .join('|');

  // -- Floating video-settings gear -----------------------------------------
  // The single selected video, if exactly one video element is selected.
  const selectedVideo = useMemo<VideoElement | null>(() => {
    if (selectedElementIds.length !== 1) return null;
    const el = elements.find((candidate) => candidate.id === selectedElementIds[0]);
    return el && el.type === 'video' ? el : null;
  }, [selectedElementIds, elements]);

  const [gearPos, setGearPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!selectedVideo || isInteracting) {
      setGearPos(null);
      return;
    }
    const compute = () => {
      const target = canvasRef.current?.querySelector(
        `[data-element-id="${CSS.escape(selectedVideo.id)}"]`
      ) as HTMLElement | null;
      const outer = outerRef.current;
      if (!target || !outer) {
        setGearPos(null);
        return;
      }
      const elRect = target.getBoundingClientRect();
      const outerRect = outer.getBoundingClientRect();
      setGearPos({
        top: elRect.top - outerRect.top + outer.scrollTop - 8,
        left: elRect.right - outerRect.left + outer.scrollLeft + 8,
      });
    };
    compute();
    const outer = outerRef.current;
    outer?.addEventListener('scroll', compute);
    window.addEventListener('resize', compute);
    return () => {
      outer?.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [selectedVideo, isInteracting, selectedPositionKey, zoom]);

  const handleVideoContentUpdate = useCallback(
    (id: string, patch: Partial<VideoElement['content']>) => {
      const el = elements.find((candidate) => candidate.id === id);
      if (!el || el.type !== 'video') return;
      updateElement(id, { content: { ...el.content, ...patch } });
    },
    [elements, updateElement]
  );

  // Click on grey area outside the canvas → deselect all
  const handleOuterClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      resetVideoPreviews();
      dispatch(clearSelection());
    },
    [dispatch, resetVideoPreviews]
  );

  // Click on blank canvas → deselect or add element via tool
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target !== canvasRef.current) return; // only on the canvas itself
      resetVideoPreviews();

      if (!canEdit) {
        dispatch(clearSelection());
        return;
      }

      const rect = canvasRef.current!.getBoundingClientRect();
      const scale = zoom / 100;
      const x = Math.round((e.clientX - rect.left) / scale);
      const y = Math.round((e.clientY - rect.top) / scale);

      switch (activeToolType) {
        case 'text':
          createTextElement('New text', x, y);
          dispatch(setActiveTool('select'));
          return;
        case 'shape':
          createShapeElement(activeShapeType, x, y);
          dispatch(setActiveTool('select'));
          return;
        case 'image':
          if (onImageToolClick) {
            onImageToolClick(x, y);
          } else {
            createImageElement({}, x, y);
          }
          dispatch(setActiveTool('select'));
          return;
        default:
          dispatch(clearSelection());
      }
    },
    [
      zoom,
      activeToolType,
      activeShapeType,
      createTextElement,
      createShapeElement,
      createImageElement,
      dispatch,
      canEdit,
      onImageToolClick,
      resetVideoPreviews,
    ]
  );

  const handleElementDoubleClick = useCallback(
    (id: string) => {
      const el = elements.find((e) => e.id === id);
      if (el?.type === 'image' && onImageDoubleClick) {
        onImageDoubleClick(id);
      }
    },
    [elements, onImageDoubleClick]
  );

  const handleTextChange = useCallback(
    (id: string, text: string) => {
      updateElement(id, { content: { text } } as never);
    },
    [updateElement]
  );

  const scale = zoom / 100;

  // In read-only mode, force cursor to default (no crosshair for tool modes)
  const canvasCursor = !canEdit ? 'default' : activeToolType !== 'select' ? 'crosshair' : 'default';

  if (!currentSlide) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        No slide selected
      </Box>
    );
  }

  return (
    <Box
      ref={outerRef}
      onWheel={handleWheel}
      onClick={handleOuterClick}
      className="slide-canvas-root"
      sx={{
        '& .moveable-control-box': {
          '--moveable-color': '#1a73e8',
        },

        '& .moveable-control': {
          border: '2px solid #1a73e8 !important',
          background: '#ffffff !important',
        },
        '& .moveable-rotation-control': {
          border: '2px solid #1a73e8 !important',
          background: '#ffffff !important',
        },
        '& .moveable-rotation-line': {
          background: '#1a73e8 !important',
          width: '1px !important',
        },
        /* Snap guide-line styling (Google Slides red) */

        '& .moveable-guideline': { background: '#db4437 !important', zIndex: '9999 !important' },
        '& .moveable-gap': { background: '#44aaff !important' },
        '& .moveable-dashed': { borderColor: '#db4437 !important' },
        '& .moveable-bold': { background: '#db4437 !important' },
        '& .moveable-guideline.moveable-bold.moveable-vertical': { width: '1px !important' },
        '& .moveable-guideline.moveable-bold.moveable-horizontal': { height: '1px !important' },
        '& .moveable-line.moveable-direction': {
          height: '2px !important',
          background: '#1a73e8 !important',
        },
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
        backgroundColor: '#e8e8e8',
        position: 'relative',
      }}
    >
      {/* Scaled canvas wrapper */}
      <div
        ref={canvasRef}
        onClick={handleCanvasClick}
        style={{
          width: SLIDE_PAGE_WIDTH,
          height: SLIDE_PAGE_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          backgroundColor: resolveSlideBackground(currentSlide, theme),
          boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
          position: 'relative',
          flexShrink: 0,
          cursor: canvasCursor,
        }}
      >
        {elements.map((el) => {
          const interactive = canEdit && el.source !== 'theme';
          return (
            <SlideElement
              key={el.id}
              element={el}
              isSelected={selectedElementIds.includes(el.id)}
              zoom={zoom}
              onSelect={(id) => selectElement(id)}
              onDoubleClick={interactive ? handleElementDoubleClick : undefined}
              onTextChange={interactive ? handleTextChange : undefined}
              onUpdateElement={interactive ? updateElement : undefined}
              canEdit={interactive}
              suppressVideoPreview={suppressVideoPreview}
              videoPreviewResetKey={videoPreviewResetKey}
            />
          );
        })}

        {/* Moveable overlay — inside canvas div so it shares the CSS scale transform */}
        {canEdit && selectedTargets.length > 0 && (
          <MoveableOverlay
            targets={selectedTargets}
            elements={elements}
            getHandlers={getHandlersTracked}
            updateElements={updateElements}
            container={canvasRef.current}
            cssScale={scale}
            siblingElementIds={elements
              .filter((el) => !selectedElementIds.includes(el.id))
              .map((el) => el.id)}
            positionKey={selectedPositionKey}
          />
        )}
      </div>
      {canEdit && selectedVideo && gearPos && (
        <VideoSettingsControl
          element={selectedVideo}
          onUpdateContent={(patch) => handleVideoContentUpdate(selectedVideo.id, patch)}
          style={{ position: 'absolute', top: gearPos.top, left: gearPos.left, zIndex: 10 }}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// MoveableOverlay — keeps the Moveable instance isolated so it only
// re-renders when the selection changes, not on every canvas re-render.
// ---------------------------------------------------------------------------

// Snap directions: edges + center of both the dragged element and sibling targets.
const SNAP_DIRECTIONS = {
  top: true,
  left: true,
  bottom: true,
  right: true,
  center: true,
  middle: true,
} as const;

// Canvas-level guide lines: edges + center.
const H_GUIDELINES = [0, SLIDE_PAGE_HEIGHT / 2, SLIDE_PAGE_HEIGHT];
const V_GUIDELINES = [0, SLIDE_PAGE_WIDTH / 2, SLIDE_PAGE_WIDTH];

// Rotation snap at 15° increments (matches Google Slides behaviour).
const SNAP_ROTATION_DEGREES = Array.from({ length: 24 }, (_, i) => i * 15);

const MoveableOverlay: React.FC<{
  targets: HTMLElement[];
  elements: ResolvedSlideElement[];
  getHandlers: ReturnType<typeof import('../hooks/useMoveable').useMoveable>['getHandlers'];
  updateElements: ReturnType<
    typeof import('../hooks/useSlideElements').useSlideElements
  >['updateElements'];
  container: HTMLElement | null;
  cssScale: number;
  /** IDs of sibling elements on the same slide (used as snap targets). */
  siblingElementIds: string[];
  /** Changes when the element's position/size changes (e.g. after nudge) so Moveable refreshes its rect. */
  positionKey: string;
}> = React.memo(
  ({
    targets,
    elements,
    getHandlers,
    updateElements,
    container,
    cssScale,
    siblingElementIds,
    positionKey,
  }) => {
    const moveableRef = useRef<Moveable>(null);
    const isGroup = targets.length > 1;
    const firstTarget = targets[0];
    const firstElementId = firstTarget?.dataset.elementId ?? '';
    const handlers = firstElementId ? getHandlers(firstElementId) : null;

    const elementsById = useMemo(() => new Map(elements.map((el) => [el.id, el])), [elements]);

    // Lock aspect ratio on corner handles for image and video elements.
    const keepRatio = useMemo(() => {
      if (isGroup) return false;
      const el = firstElementId ? elementsById.get(firstElementId) : undefined;
      return el?.type === 'image' || el?.type === 'video';
    }, [isGroup, firstElementId, elementsById]);

    // Videos can't be rotated — Google Slides export rejects a rotated video transform.
    const rotatable = useMemo(() => {
      if (isGroup) return false;
      const el = firstElementId ? elementsById.get(firstElementId) : undefined;
      return el?.type !== 'video';
    }, [isGroup, firstElementId, elementsById]);

    // Resolve sibling DOM elements for Moveable's elementGuidelines.
    const elementGuidelines = useMemo(() => {
      if (!container) return [];
      return siblingElementIds
        .map(
          (id) =>
            container.querySelector(`[data-element-id="${CSS.escape(id)}"]`) as HTMLElement | null
        )
        .filter(Boolean) as HTMLElement[];
    }, [container, siblingElementIds]);

    const handleDragGroup = useCallback(
      (event: OnDragGroup) => {
        const items: Array<{ target: HTMLElement; x: number; y: number; w: number; h: number }> =
          [];
        event.events.forEach((childEvent) => {
          if (!(childEvent.target instanceof HTMLElement)) return;
          const id = childEvent.target.dataset.elementId;
          const el = id ? elementsById.get(id) : undefined;
          if (!el) return;
          items.push({
            target: childEvent.target,
            x: childEvent.left,
            y: childEvent.top,
            w: el.position.width,
            h: el.position.height,
          });
        });
        if (items.length === 0) return;
        const { dx, dy } = clampGroupDelta(items);
        for (const item of items) {
          item.target.style.left = `${Math.round(item.x + dx)}px`;
          item.target.style.top = `${Math.round(item.y + dy)}px`;
        }
      },
      [elementsById]
    );

    const handleDragGroupEnd = useCallback(
      (event: OnDragGroupEnd) => {
        const items: Array<{ id: string; el: (typeof elements)[number]; x: number; y: number }> =
          [];
        event.events.forEach((childEvent) => {
          const lastEvent = childEvent.lastEvent as OnDrag | undefined;
          if (!(childEvent.target instanceof HTMLElement) || !lastEvent) return;
          const id = childEvent.target.dataset.elementId;
          const el = id ? elementsById.get(id) : undefined;
          if (!id || !el) return;
          items.push({ id, el, x: lastEvent.left, y: lastEvent.top });
        });
        if (items.length === 0) return;
        const { dx, dy } = clampGroupDelta(
          items.map(({ el, x, y }) => ({ x, y, w: el.position.width, h: el.position.height }))
        );
        const updates = items.map(({ id, el, x, y }) => ({
          id,
          updates: { position: { ...el.position, x: Math.round(x + dx), y: Math.round(y + dy) } },
        }));
        updateElements(updates);
      },
      [elementsById, updateElements]
    );

    // After a nudge (or any external position change), tell Moveable to re-read the target rect.
    useEffect(() => {
      moveableRef.current?.updateRect();
    }, [positionKey, targets]);

    if (!firstTarget || !handlers) return null;

    return (
      <Moveable
        ref={moveableRef}
        target={isGroup ? targets : firstTarget}
        container={container}
        draggable
        resizable={!isGroup}
        keepRatio={keepRatio}
        rotatable={rotatable}
        throttleDrag={0}
        throttleResize={0}
        throttleRotate={0}
        /* Keep handle sizes constant regardless of canvas zoom */
        zoom={1 / cssScale}
        origin={false}
        renderDirections={['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se']}
        /* ---- Snapping ---- */
        snappable
        snapDirections={SNAP_DIRECTIONS}
        elementSnapDirections={SNAP_DIRECTIONS}
        elementGuidelines={elementGuidelines}
        horizontalGuidelines={H_GUIDELINES}
        verticalGuidelines={V_GUIDELINES}
        snapThreshold={5}
        isDisplaySnapDigit={false}
        maxSnapElementGuidelineDistance={300}
        snapRotationDegrees={SNAP_ROTATION_DEGREES}
        snapRotationThreshold={3}
        /* ---- Handlers ---- */
        hideChildMoveableDefaultLines={isGroup}
        onDragStart={isGroup ? undefined : handlers.onDragStart}
        onDrag={isGroup ? undefined : handlers.onDrag}
        onDragEnd={isGroup ? undefined : handlers.onDragEnd}
        onDragGroup={isGroup ? handleDragGroup : undefined}
        onDragGroupEnd={isGroup ? handleDragGroupEnd : undefined}
        onResizeStart={isGroup ? undefined : handlers.onResizeStart}
        onResize={isGroup ? undefined : handlers.onResize}
        onResizeEnd={isGroup ? undefined : handlers.onResizeEnd}
        onRotateStart={isGroup ? undefined : handlers.onRotateStart}
        onRotate={isGroup ? undefined : handlers.onRotate}
        onRotateEnd={isGroup ? undefined : handlers.onRotateEnd}
      />
    );
  }
);
MoveableOverlay.displayName = 'MoveableOverlay';

/**
 * Compute a uniform (dx, dy) correction so no element in the group can
 * fully leave the canvas on any side.  Applied identically to every
 * element so relative positions are preserved.
 */
const clampGroupDelta = (
  items: Array<{ x: number; y: number; w: number; h: number }>
): { dx: number; dy: number } => {
  if (items.length === 0) return { dx: 0, dy: 0 };
  let maxX = -Infinity,
    maxY = -Infinity,
    minRight = Infinity,
    minBottom = Infinity;
  for (const { x, y, w, h } of items) {
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
    minRight = Math.min(minRight, x + w);
    minBottom = Math.min(minBottom, y + h);
  }
  let dx = 0,
    dy = 0;
  if (minRight < 0) dx = -minRight;
  else if (maxX > SLIDE_PAGE_WIDTH) dx = SLIDE_PAGE_WIDTH - maxX;
  if (minBottom < 0) dy = -minBottom;
  else if (maxY > SLIDE_PAGE_HEIGHT) dy = SLIDE_PAGE_HEIGHT - maxY;
  return { dx, dy };
};

export default SlideCanvas;
